import { useEffect, useRef } from 'react';

/**
 * Runs `callback` once `delay` ms after mount, with `reset` to restart the wait and `clear`
 * to cancel it. The timer handle and the latest callback live in refs, so `reset` and
 * `clear` work from any render's closure and a re-render never restarts the timer.
 */
export const useTimeout = (callback: () => void, delay: number) => {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    callbackRef.current = callback;
  });

  const clear = () => {
    if (timeoutRef.current !== undefined) clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
  };

  const set = () => {
    timeoutRef.current = setTimeout(() => callbackRef.current(), delay);
  };

  const reset = () => {
    clear();
    set();
  };

  useEffect(() => {
    set();
    return clear;
  }, [delay]);

  return { reset, clear };
};
